import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FaTimes, FaTrophy, FaUsers, FaCalendarAlt, FaSearch } from 'react-icons/fa';
import styles from './AwardAssignmentModal.module.css';
import awardsAPI from '../../api/awards';
import { getAuth } from 'firebase/auth';
import { formatRole as formatRoleUtil, loadRolesForSport } from '../../utils/roleFormatting';

const AwardAssignmentModal = ({ 
  programId, 
  programSport, 
  onClose, 
  onAwardCreated, 
  selectedEvent = null 
}) => {
  const [step, setStep] = useState(1);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [notes, setNotes] = useState('');
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState(selectedEvent?._id || '');
  const [players, setPlayers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [eventSearchTerm, setEventSearchTerm] = useState('');
  const [templateSearchTerm, setTemplateSearchTerm] = useState('');
  const [isEventDropdownOpen, setIsEventDropdownOpen] = useState(false);
  const [isPlayerDropdownOpen, setIsPlayerDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Load sport roles from database for role display formatting
  useEffect(() => {
    if (programSport) loadRolesForSport(programSport);
  }, [programSport]);

  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch award templates for the program's sport
        const templatesData = await awardsAPI.getTemplates(programSport, { isActive: true });
        setTemplates(templatesData);

        // Fetch events for the program
        try {
          const eventsResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/events/${programId}`);
          if (eventsResponse.ok) {
            const eventsData = await eventsResponse.json();
            setEvents(Array.isArray(eventsData) ? eventsData : []);
          }
        } catch (eventError) {
          console.error('Error fetching events:', eventError);
          // Continue without events
        }

        // Fetch teams first (like Teams page does)
        let teamsData = [];
        try {
          const teamsResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/teams/program/${programId}`);
          if (teamsResponse.ok) {
            const teams = await teamsResponse.json();
            teamsData = Array.isArray(teams) ? teams : [teams];
          }
        } catch (teamError) {
          console.error('Error fetching teams:', teamError);
        }

        // Fetch players using the same endpoint as Teams page
        try {
          const playersResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/teams/program/${programId}/available-players`);
          if (playersResponse.ok) {
            const playersData = await playersResponse.json();
            
            if (playersData.availablePlayers && Array.isArray(playersData.availablePlayers)) {
              
              // Create team lookup map - use userId to match with availablePlayers
              const teamMap = {};
              teamsData.forEach(team => {
                if (team.players && Array.isArray(team.players)) {
                  team.players.forEach(player => {
                    // Use userId field to match with available players
                    const playerId = player.userId || player._id;
                    teamMap[playerId] = {
                      teamId: team._id,
                      teamName: team.name,
                      roleKey: player.roleKey || 'player'
                    };
                  });
                }
              });
              
              // Transform to match Teams page format
              const transformedPlayers = playersData.availablePlayers.map(player => {
                const teamInfo = teamMap[player._id] || {};
                
                return {
                  userId: player._id,
                  firstName: player.firstName || 'Unknown',
                  lastName: player.lastName || 'Player',
                  teamId: teamInfo.teamId || player.teamId || player.team?._id || player.currentTeam?._id || null,
                  teamName: teamInfo.teamName || player.teamName || player.team?.name || player.currentTeam?.name || 'No Team',
                  roleKey: teamInfo.roleKey || player.roleKey || 'player',
                  roleName: player.roleName || 'Player',
                  // Additional fields from Teams page
                  fullName: player.fullName || `${player.firstName || ''} ${player.lastName || ''}`.trim(),
                  roleContext: player.roleContext || 'Player',
                  programRole: player.programRole || 'player'
                };
              });
              setPlayers(transformedPlayers);
            }
          }
        } catch (playerError) {
          console.error('Error fetching players:', playerError);
          // Try fallback method
          try {
            const fallbackResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/programs/${programId}/members`);
            if (fallbackResponse.ok) {
            const fallbackData = await fallbackResponse.json();
              
              if (Array.isArray(fallbackData)) {
                const transformedPlayers = fallbackData
                  .filter(member => member.role === 'Player' || member.role === 'player')
                  .map(member => ({
                    userId: member.userId._id || member.userId,
                    firstName: member.userId.firstName || 'Unknown',
                    lastName: member.userId.lastName || 'Player',
                    teamId: null,
                    teamName: 'No Team',
                    roleKey: 'player',
                    roleName: 'Player',
                    fullName: `${member.userId.firstName || ''} ${member.userId.lastName || ''}`.trim(),
                    roleContext: 'Player',
                    programRole: 'player'
                  }));
                setPlayers(transformedPlayers);
              }
            }
          } catch (fallbackError) {
            console.error('Error with fallback method:', fallbackError);
          }
        }

      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to load data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [programId, programSport]);

  // Format role names using data-driven utility (roles loaded from database)
  const formatRole = formatRoleUtil;

  // Get display text for selected event
  const getSelectedEventDisplayText = () => {
    if (selectedEventId === '') {
      return 'Series Award (No specific event)';
    }
    
    const selectedEvent = events.find(event => event._id === selectedEventId);
    
    if (selectedEvent) {
      const eventDate = new Date(selectedEvent.dateTime?.start || selectedEvent.date);
      const formattedDate = eventDate.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
      const eventType = selectedEvent.type || 'Event';
      return `${selectedEvent.title} - ${formattedDate} - ${eventType}`;
    }
    
    return 'Select an event...';
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setStep(2);
  };

  const handleRecipientToggle = (player) => {
    setSelectedRecipients(prev => {
      const isSelected = prev.some(p => p.userId === player.userId);
      if (isSelected) {
        return prev.filter(p => p.userId !== player.userId);
      } else {
        return [...prev, {
          id: player.userId, // Backend expects 'id' field
          userId: player.userId,
          playerName: `${player.firstName} ${player.lastName}`,
          teamId: player.teamId,
          teamName: player.teamName,
          roleKey: player.roleKey,
          roleName: player.roleName
        }];
      }
    });
  };

  // Event dropdown interaction functions
  const handleEventSearchFocus = () => {
    setIsEventDropdownOpen(true);
  };

  const handleEventSearchChange = (e) => {
    setEventSearchTerm(e.target.value);
    setIsEventDropdownOpen(true);
  };

  const handleEventSelect = (eventId) => {
    setSelectedEventId(eventId);
    setIsEventDropdownOpen(false);
    setEventSearchTerm('');
  };

  const handleEventDropdownBlur = (e) => {
    // Check if the blur is caused by clicking on a dropdown option
    const relatedTarget = e.relatedTarget;
    if (relatedTarget && relatedTarget.closest(`.${styles.dropdownContent}`)) {
      return; // Don't close if clicking on dropdown content
    }
    
    // Delay closing to allow for click events
    setTimeout(() => {
      setIsEventDropdownOpen(false);
    }, 150);
  };

  const handleDropdownOptionClick = (eventId, e) => {
    e.preventDefault();
    e.stopPropagation();
    handleEventSelect(eventId);
  };

  // Player dropdown interaction functions
  const handlePlayerSearchFocus = () => {
    setIsPlayerDropdownOpen(true);
  };

  const handlePlayerSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setIsPlayerDropdownOpen(true);
  };

  const handlePlayerDropdownBlur = (e) => {
    // Check if the blur is caused by clicking on a dropdown option
    const relatedTarget = e.relatedTarget;
    if (relatedTarget && relatedTarget.closest(`.${styles.dropdownContent}`)) {
      return; // Don't close if clicking on dropdown content
    }
    
    // Delay closing to allow for click events
    setTimeout(() => {
      setIsPlayerDropdownOpen(false);
    }, 150);
  };

  const handlePlayerOptionClick = (player, e) => {
    e.preventDefault();
    e.stopPropagation();
    handleRecipientToggle(player);
    // Keep dropdown open for multi-select
  };

  const handleSubmit = async () => {
    if (!selectedTemplate || selectedRecipients.length === 0) {
      setError('Please select an award template and at least one recipient.');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Get Firebase auth token
      const auth = getAuth();
      if (!auth.currentUser) {
        throw new Error('User not authenticated');
      }
      const token = await auth.currentUser.getIdToken();

      const awardData = {
        eventId: selectedEventId || undefined,
        awardTemplateId: selectedTemplate._id,
        recipients: selectedRecipients,
        notes: notes.trim() || undefined
      };

      // Remove undefined fields to avoid backend issues
      const cleanAwardData = Object.fromEntries(
        Object.entries(awardData).filter(([_, value]) => value !== undefined)
      );

      await awardsAPI.createAward(programId, cleanAwardData, token);
      
      // Call success callback
      if (onAwardCreated) {
        onAwardCreated();
      }
      
      // Close modal
      onClose();
      
    } catch (error) {
      console.error('Error creating award:', error);
      setError(error.message || 'Failed to create award. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const filteredPlayers = players.filter(player => {
    const fullName = `${player.firstName} ${player.lastName}`.toLowerCase();
    return fullName.includes(searchTerm.toLowerCase());
  });

  const filteredEvents = events
    .filter(event => event.status === 'Completed') // Only show completed events
    .filter(event => {
      if (!eventSearchTerm) return true;
      const searchLower = eventSearchTerm.toLowerCase();
      const eventTitle = event.title?.toLowerCase() || '';
      const eventType = event.type?.toLowerCase() || '';
      const eventDate = new Date(event.dateTime?.start || event.date).toLocaleDateString().toLowerCase();
      return eventTitle.includes(searchLower) || eventType.includes(searchLower) || eventDate.includes(searchLower);
    });

  const filteredTemplates = templates.filter(template => {
    if (!templateSearchTerm) return true;
    const searchLower = templateSearchTerm.toLowerCase();
    const awardName = template.awardName?.toLowerCase() || '';
    const description = template.description?.toLowerCase() || '';
    const awardType = template.awardType?.toLowerCase() || '';
    const category = template.category?.toLowerCase() || '';
    return awardName.includes(searchLower) || 
           description.includes(searchLower) || 
           awardType.includes(searchLower) || 
           category.includes(searchLower);
  });

  const getAwardIcon = (awardType) => {
    switch (awardType) {
      case 'individual': return <FaTrophy />;
      case 'team': return <FaUsers />;
      case 'position': return <FaTrophy />;
      case 'statistical': return <FaTrophy />;
      case 'character': return <FaTrophy />;
      default: return <FaTrophy />;
    }
  };

  const getAwardColor = (awardType) => {
    switch (awardType) {
      case 'individual': return '#FFD700';
      case 'team': return '#C0C0C0';
      case 'position': return '#CD7F32';
      case 'statistical': return '#FF8C00';
      case 'character': return '#90EE90';
      default: return '#800080';
    }
  };

  const formatCategoryText = (text) => {
    if (!text) return '';
    // Add space before "match", "series", "season" and capitalize first letter
    let formatted = text
      .replace(/match/gi, ' Match')
      .replace(/series/gi, ' Series')
      .replace(/season/gi, ' Season')
      .trim();
    
    // Capitalize first letter
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  };

  return createPortal(
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className={styles.modalHeader}>
          <div className={styles.modalTitle}>
            <FaTrophy className={styles.modalTitleIcon} />
            Assign Award
          </div>
          <div className={styles.headerControls}>
            <button className={styles.closeButton} onClick={onClose} aria-label="Close">
              <FaTimes />
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <div className={styles.modalBody}>
          <form className={styles.form}>
          {error && (
            <div className={styles.error}>
              {error}
            </div>
          )}

          {loading && (
            <div className={styles.loading}>
              <div className={styles.spinner}></div>
              <p>Loading...</p>
            </div>
          )}

          {/* Step 1: Select Award Template */}
          {step === 1 && !loading && (
            <div className={styles.step}>
              <div className={styles.stepHeader}>
                <h3>Select Award Template</h3>
                <div className={styles.templateSearch}>
                  <FaSearch className={styles.searchIcon} />
                  <input
                    type="text"
                    placeholder="Search templates..."
                    value={templateSearchTerm}
                    onChange={(e) => setTemplateSearchTerm(e.target.value)}
                    className={styles.searchInput}
                  />
                </div>
              </div>
              <div className={styles.templatesGrid}>
                {filteredTemplates.map((template) => (
                  <div
                    key={template._id}
                    className={`${styles.templateCard} ${selectedTemplate?._id === template._id ? styles.selected : ''}`}
                    onClick={() => handleTemplateSelect(template)}
                  >
                    <div className={styles.templateIcon} style={{ color: getAwardColor(template.awardType) }}>
                      {getAwardIcon(template.awardType)}
                    </div>
                    <div className={styles.templateInfo}>
                      <div className={styles.templateName}>{template.awardName}</div>
                      <div className={styles.templateDescription}>{template.description}</div>
                      <div className={styles.templateTags}>
                        <span className={styles.templateType}>{formatCategoryText(template.awardType)}</span>
                        <span className={styles.templateCategory}>{formatCategoryText(template.category)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {filteredTemplates.length === 0 && templateSearchTerm && (
                <div className={styles.noResults}>
                  <p>No templates found matching "{templateSearchTerm}"</p>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Select Recipients */}
          {step === 2 && !loading && (
            <div className={styles.step}>
              <div className={styles.stepHeader}>
                <h3>Select Recipients</h3>
                <button 
                  className={styles.backButton}
                  onClick={() => setStep(1)}
                >
                  Back
                </button>
              </div>

              {selectedTemplate && (
                <div className={styles.selectedTemplate}>
                  <div className={styles.templateIcon} style={{ color: getAwardColor(selectedTemplate.awardType) }}>
                    {getAwardIcon(selectedTemplate.awardType)}
                  </div>
                  <div>
                    <h4>{selectedTemplate.awardName}</h4>
                    <p>{selectedTemplate.description}</p>
                  </div>
                </div>
              )}

              {/* Event Selection */}
              <div className={styles.eventSelection}>
                <label>
                  <FaCalendarAlt className={styles.labelIcon} />
                  Event (Optional)
                </label>
                
                {/* Searchable Event Dropdown */}
                <div className={styles.searchableDropdown}>
                  <div className={`${styles.dropdownHeader} ${selectedEventId ? styles.hasSelection : ''}`}>
                    <FaSearch className={styles.searchIcon} />
                    <input
                      type="text"
                      placeholder={isEventDropdownOpen ? "Search events..." : getSelectedEventDisplayText()}
                      value={eventSearchTerm}
                      onChange={handleEventSearchChange}
                      onFocus={handleEventSearchFocus}
                      onBlur={handleEventDropdownBlur}
                      className={`${styles.searchInput} ${selectedEventId ? styles.selectedInput : ''}`}
                      readOnly={!isEventDropdownOpen}
                    />
                    {selectedEventId && !isEventDropdownOpen && (
                      <div className={styles.selectionIndicator}>
                        <div className={styles.checkmark}>✓</div>
                      </div>
                    )}
                  </div>
                  
                  {/* Dropdown Content - Only show when expanded */}
                  {isEventDropdownOpen && (
                    <div className={styles.dropdownContent}>
                      {/* Series Award Option */}
                      <div
                        className={`${styles.dropdownOption} ${selectedEventId === '' ? styles.selected : ''}`}
                        onClick={(e) => handleDropdownOptionClick('', e)}
                        onMouseDown={(e) => e.preventDefault()} // Prevent input blur
                      >
                        <div className={styles.optionContent}>
                          <span className={styles.optionTitle}>Series Award (No specific event)</span>
                          <span className={styles.optionSubtitle}>Award not tied to a specific match</span>
                        </div>
                      </div>
                      
                      {/* Event Options */}
                      {filteredEvents.map((event) => {
                        const eventDate = new Date(event.dateTime?.start || event.date);
                        const formattedDate = eventDate.toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        });
                        const eventType = event.type || 'Event';
                        const isSelected = selectedEventId === event._id;
                        
                        return (
                          <div
                            key={event._id}
                            className={`${styles.dropdownOption} ${isSelected ? styles.selected : ''}`}
                            onClick={(e) => handleDropdownOptionClick(event._id, e)}
                            onMouseDown={(e) => e.preventDefault()} // Prevent input blur
                          >
                            <div className={styles.optionContent}>
                              <span className={styles.optionTitle}>{event.title}</span>
                              <span className={styles.optionSubtitle}>{formattedDate} - {eventType}</span>
                            </div>
                          </div>
                        );
                      })}
                      
                      {/* No Results */}
                      {events.length > 0 && filteredEvents.length === 0 && eventSearchTerm && (
                        <div className={styles.noResults}>
                          <p>No events found matching "{eventSearchTerm}"</p>
                        </div>
                      )}
                      
                      {/* No Events */}
                      {events.length === 0 && (
                        <div className={styles.noResults}>
                          <p>No events available for this program</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Player Selection - Searchable Dropdown */}
              <div className={styles.formGroupFull}>
                <label className={styles.formLabel}>
                  <FaUsers className={styles.formLabelIcon} />
                  Select Recipients
                </label>
                
                {/* Searchable Player Dropdown */}
                <div className={`${styles.searchableDropdown} ${selectedRecipients.length > 0 ? styles.hasSelection : ''}`}>
                  <div className={`${styles.dropdownHeader} ${selectedRecipients.length > 0 ? styles.hasSelection : ''}`}>
                    <FaSearch className={styles.searchIcon} />
                    <input
                      type="text"
                      placeholder={isPlayerDropdownOpen ? "Search players..." : `${selectedRecipients.length} player(s) selected - Click to select more`}
                      value={searchTerm}
                      onChange={handlePlayerSearchChange}
                      onFocus={handlePlayerSearchFocus}
                      onBlur={handlePlayerDropdownBlur}
                      className={`${styles.searchInput} ${selectedRecipients.length > 0 ? styles.selectedInput : ''}`}
                      readOnly={!isPlayerDropdownOpen}
                    />
                    {selectedRecipients.length > 0 && !isPlayerDropdownOpen && (
                      <div className={styles.selectionIndicator}>
                        <div className={styles.checkmark}>{selectedRecipients.length}</div>
                      </div>
                    )}
                  </div>
                  
                  {/* Dropdown Content */}
                  {isPlayerDropdownOpen && (
                    <div className={styles.dropdownContent}>
                      {filteredPlayers.length === 0 ? (
                        <div className={styles.noResults}>
                          <p>{searchTerm ? `No players found matching "${searchTerm}"` : 'No players available'}</p>
                        </div>
                      ) : (
                        filteredPlayers.map((player) => {
                          const isSelected = selectedRecipients.some(p => p.userId === player.userId);
                          return (
                            <div
                              key={player.userId}
                              className={`${styles.dropdownOption} ${isSelected ? styles.selected : ''}`}
                              onClick={(e) => handlePlayerOptionClick(player, e)}
                              onMouseDown={(e) => e.preventDefault()}
                            >
                              <div className={styles.optionContent}>
                                <span className={styles.optionTitle}>
                                  {isSelected && '✓ '}
                                  {player.fullName || `${player.firstName} ${player.lastName}`}
                                </span>
                                <span className={styles.optionSubtitle}>
                                  {player.teamName || 'No Team'} • {formatRole(player.roleKey)}
                                </span>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
              </div>

              {selectedRecipients.length > 0 && (
                <div className={styles.selectedRecipients}>
                  <h4>Selected Recipients ({selectedRecipients.length})</h4>
                  <div className={styles.selectedRecipientsList}>
                    {selectedRecipients.map((recipient, index) => (
                      <div key={index} className={styles.selectedRecipient}>
                        {recipient.playerName}
                        <button
                          onClick={() => handleRecipientToggle({ userId: recipient.userId })}
                          className={styles.removeButton}
                          type="button"
                        >
                          <FaTimes />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Notes */}
              <div className={styles.formGroupFull}>
                <label className={styles.formLabel}>
                  <FaCalendarAlt className={styles.formLabelIcon} />
                  Notes (Optional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add any additional notes about this award..."
                  className={styles.notesTextarea}
                  rows={3}
                />
              </div>

              {/* Submit Button */}
            </div>
          )}
          </form>
        </div>

        {/* Fixed Button Container */}
        <div className={styles.buttonContainer}>
          <button
            className={styles.cancelButton}
            onClick={onClose}
            type="button"
          >
            Cancel
          </button>
          <button
            className={styles.createButton}
            onClick={handleSubmit}
            disabled={selectedRecipients.length === 0 || loading}
            type="button"
          >
            {loading ? 'Creating Award...' : 'Create Award'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AwardAssignmentModal;
